import { useCallback, useEffect, useState } from "react";

import type { LobKey } from "src/jsonForms/preScreen/types";

import { routeLineOfBusiness } from "./registry";
import { resolveSearchConfig } from "./searchConfigResolver";
import type { SearchConfigFailure } from "./searchConfigResolver";
import type { SearchConfig } from "./types";

export type SearchConfigState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly lob: LobKey; readonly config: SearchConfig }
  | {
      readonly status: "failed";
      readonly failure: SearchConfigFailure;
      /** Issues one further retrieval. Safe to call at any time. */
      readonly retry: () => void;
      /**
       * True while a retry this hook issued is in flight. The failure stays on
       * screen meanwhile, rather than giving way to the loading indicator, so the
       * control that issued the retry is still there to show it is working.
       */
      readonly retrying: boolean;
    };

type Resolution =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly lob: LobKey; readonly config: SearchConfig }
  | { readonly status: "failed"; readonly failure: SearchConfigFailure };

/**
 * Resolves the Search_Config for a claims history route, one retrieval per
 * resolved line of business, and one more per retry.
 */
export function useSearchConfig(pathname: string): SearchConfigState {
  /**
   * Keyed on the resolved line of business, so a change to a part of the path
   * the resolver ignores — a query string, a fragment, a trailing slash — does
   * not spend a second retrieval. An unregistered route resolves no line of
   * business and falls back to the path itself, so a move between two
   * unregistered paths still reports the path the user is on.
   */
  const resolutionKey: string = routeLineOfBusiness(pathname) ?? pathname;

  const [resolution, setResolution] = useState<Resolution>({ status: "loading" });
  const [renderedKey, setRenderedKey] = useState(resolutionKey);
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);

  // Discarded during render rather than in an effect, so a line-of-business
  // change cannot paint the previous line of business's table for a frame
  // before the loading indicator replaces it.
  if (renderedKey !== resolutionKey) {
    setRenderedKey(resolutionKey);
    setResolution({ status: "loading" });
    setRetrying(false);
  }

  useEffect(() => {
    // One per attempt. Aborting it ends this attempt's wait and nothing else:
    // the resolver shares the request with any other caller, and lets it finish
    // to warm the memo.
    const controller = new AbortController();

    void resolveSearchConfig(pathname, controller.signal).then((outcome) => {
      // A retrieval that settles after an unmount, a line-of-business change, or
      // a newer attempt belongs to a page the user has left, so its outcome is
      // dropped rather than written over the state the current route is showing.
      if (controller.signal.aborted || (!outcome.ok && "aborted" in outcome)) {
        return;
      }
      setRetrying(false);
      setResolution(
        outcome.ok
          ? { status: "ready", lob: outcome.lob, config: outcome.config }
          : { status: "failed", failure: outcome.failure }
      );
    });

    return () => controller.abort();
    // Deliberately keyed on the resolved line of business and the attempt.
    // `pathname` is read inside but is not a dependency: the parts of it that
    // can change without changing the key are the parts the resolver discards.
  }, [resolutionKey, attempt]);

  const retry = useCallback(() => {
    setRetrying(true);
    setAttempt((previous) => previous + 1);
  }, []);

  return resolution.status === "failed" ? { ...resolution, retry, retrying } : resolution;
}
